import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import Header from "./Header";

function PromotionalComment() {
  const { image, type } = useParams();
  const initialPromotionCommentId = image;
  const dealerId = localStorage.getItem("dealer_id");
  const navigate = useNavigate();

  const [comments, setComments] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [oldPromotionCommentIds, setOldPromotionCommentIds] = useState("");
  const scrollContainerRef = useRef(null);
  const server_url = localStorage.getItem("server_url");
  const isFetching = useRef(false);

  // Fetch comments
  const fetchComments = useCallback(
    async (oldIds = "") => {
      if (isFetching.current) return;
      isFetching.current = true;
      setLoading(true);
      try {
        const formData = new FormData();
        formData.append("company_id", "2");
        formData.append("dealer_id", dealerId);
        formData.append(
          "promotion_comment_id",
          initialPromotionCommentId || ""
        );
        formData.append("old_promotion_comment_id", oldIds);
        const res = await axios.post(
          `${server_url}promotion_comment_scroll.php`,
          formData
        );
        if (res.data.status === "1") {
          const newComments = res.data.result;
          if (newComments.length > 0) {
            setComments((prev) => {
              const existingIds = new Set(
                prev.map((c) => c.promotion_comment_id)
              );
              const filteredNewComments = newComments.filter(
                (c) => !existingIds.has(c.promotion_comment_id)
              );
              return [...prev, ...filteredNewComments];
            });
            const newOldIds = newComments
              .map((c) => c.promotion_comment_id)
              .join(",");
            setOldPromotionCommentIds((prev) =>
              prev ? `${prev},${newOldIds}` : newOldIds
            );
          }
        } else {
          toast.warning(res.data.error || "Failed to fetch comments");
        }
      } catch (e) {
        toast.error("Please try again later.");
      } finally {
        setLoading(false);
        isFetching.current = false;
      }
    },
    [server_url, dealerId, initialPromotionCommentId]
  );

  // Initial fetch
  useEffect(() => {
    fetchComments("");
  }, [fetchComments]);

  // Debounced scroll handler
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  // Handle scroll and preload
  useEffect(() => {
    const handleScroll = debounce(() => {
      if (scrollContainerRef.current) {
        const scrollTop = scrollContainerRef.current.scrollTop;
        const itemHeight = window.innerHeight;
        const newIndex = Math.round(scrollTop / itemHeight);
        if (newIndex !== currentIndex) {
          setCurrentIndex(newIndex);
          if (newIndex + 2 >= comments.length) {
            const nextOldIds = comments
              .slice(0, newIndex + 1)
              .map((c) => c.promotion_comment_id)
              .join(",");
            fetchComments(nextOldIds);
          }
        }
      }
    }, 100);

    const scrollContainer = scrollContainerRef.current;
    scrollContainer?.addEventListener("scroll", handleScroll, {
      passive: true,
    });
    return () => scrollContainer?.removeEventListener("scroll", handleScroll);
  }, [currentIndex, comments, fetchComments]);

  // Handle navigation
  const handleNavigation = (item) => {
    if (item.product_id) {
      navigate(
        `/ProductDetails/${item.product_id}/${item.product_design_name
          .trim()
          .split(/\s+/)
          .map(
            (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          )
          .join("")}`
      );
    } else if (item.promotion_id && item.promotion_type) {
      navigate(`/PromotionalImage/${item.promotion_id}/${item.promotion_type}`);
    }
  };

  // Inline styles - UPDATED FOR WHITE THEME
  const styles = {
    promotionalReelsContainer: {
      height: "100vh",
      overflowY: "scroll",
      scrollSnapType: "y mandatory",
      WebkitOverflowScrolling: "touch",
      background: "#ffffff", // Changed from #000 to #ffffff
      touchAction: "pan-y pinch-zoom",
      position: "relative",
      scrollBehavior: "smooth",
      marginTop: "30px",
    },
    reelItem: {
      height: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      scrollSnapAlign: "start",
      position: "relative",
    },
    reelContent: {
      position: "relative",
      width: "100%",
      maxWidth: "400px",
      height: "80%",
      marginTop: "20px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#f8f9fa", // Changed from rgb(32 37 42) to light gray
      borderRadius: "10px",
      padding: "20px",
      color: "#333333", // Changed from #ffffffff to dark gray
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)", // Added shadow for better visibility
      border: "1px solid #e0e0e0", // Added border for definition
    },
    commentText: {
      fontSize: "1.2rem",
      textAlign: "center",
      color: "#333333", // Changed from white to dark gray
      wordBreak: "break-word",
      lineHeight: "1.5",
    },
    commentMeta: {
      color: "#666666", // Changed from white to medium gray
      width: "90%",
      textAlign: "right",
      position: "absolute",
      bottom: "80px",
      left: "10px",
      fontSize: "0.9rem",
    },
    viewButton: {
      position: "absolute",
      bottom: "20px",
      background: "#007bff", // Changed from #ffffff to blue
      color: "#ffffff", // Changed from #000 to white
      border: "none",
      padding: "10px 20px",
      borderRadius: "20px",
      cursor: "pointer",
      fontSize: "1rem",
      zIndex: 10,
      fontWeight: "500",
      transition: "background-color 0.3s ease",
    },
    loader: {
      display: comments.length === 0 && loading ? "block" : "none",
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "50px",
      height: "50px",
      border: "5px solid #f3f3f3",
      borderTop: "5px solid #007bff", // Changed from #3498db to match theme
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
      zIndex: 1000,
    },
  };

  // Inline keyframes for loader animation
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = `
      @keyframes spin {
        0% { transform: translate(-50%, -50%) rotate(0deg); }
        100% { transform: translate(-50%, -50%) rotate(360deg); }
      }
    `;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);

  return (
    <>
      <div
        className="header fixed top-0 left-0 w-full z-50"
        style={{ background: "transparent", position: "fixed", zIndex: 9999 }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            margin: "clamp(10px, 5%, 20px)",
            gap: "8px",
          }}
        >
          <button
            onClick={() => navigate(-1)}
            style={{
              background: "#ffffff", // Changed from #00000096 to white
              height: "38px",
              width: "38px",
              border: "1px solid #e0e0e0", // Changed from 0.5px solid #fff to light gray
              borderRadius: "15px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)", // Added subtle shadow
            }}
          >
            <i
              className="fa fa-angle-left"
              style={{ fontSize: "20px", color: "#333333" }} // Added color
            ></i>
          </button>
        </div>
      </div>
      <div ref={scrollContainerRef} style={styles.promotionalReelsContainer}>
        {comments.map((item, index) => (
          <div key={item.promotion_comment_id} style={styles.reelItem}>
            <div style={styles.reelContent}>
              <p style={styles.commentText}>{item.promotion_comment}</p>
              <div style={styles.commentMeta}>
                <span style={{ fontSize: "10px", lineHeight: "normal" }}>
                  {item.dealer_firm_name}
                </span>
                <br />
                <span
                  style={{
                    fontSize: "10px",
                    lineHeight: "normal",
                    color: "#666666", // Updated to match theme
                  }}
                >
                  {item.c_date}
                </span>
              </div>
              {(item.product_id ||
                (item.promotion_id && item.promotion_type)) && (
                <button
                  onClick={() => handleNavigation(item)}
                  style={styles.viewButton}
                  onMouseEnter={(e) => e.target.style.backgroundColor = "#0056b3"}
                  onMouseLeave={(e) => e.target.style.backgroundColor = "#007bff"}
                >
                  {item.product_id ? "View Product" : "View Promotion"}
                </button>
              )}
            </div>
          </div>
        ))}
        {loading && comments.length === 0 && <div style={styles.loader}></div>}
      </div>
    </>
  );
}

export default PromotionalComment;